import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { twx } from '@ctw/shared/utils/tailwind';
import { uniqueId } from 'lodash-es';
import { type PropsWithChildren, type ReactNode, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { type PlacesType, Tooltip as ReactTooltip } from 'react-tooltip';

export interface TooltipProps {
  id: string;
  className?: string;
  tooltipClassName?: string;
  children: ReactNode;
  place?: PlacesType;
  hidden?: boolean;
  content: string;
  noArrow?: boolean;
}

export function Tooltip({
  id,
  className,
  hidden,
  place = 'bottom',
  tooltipClassName,
  children,
  content,
  noArrow = false,
}: TooltipProps) {
  const telemetry = useTelemetry();
  const instanceId = useMemo(() => `tooltip-${id}-instance${uniqueId()}`, [id]);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  return (
    <>
      <div data-tooltip-id={instanceId} className={twx(className)}>
        {children}
      </div>
      <TooltipPortal>
        <ReactTooltip
          positionStrategy="fixed"
          place={place}
          noArrow={noArrow}
          id={instanceId}
          html={content}
          className={twx('z-tooltip', { hidden }, tooltipClassName)}
          afterShow={() => {
            timer.current = setTimeout(() => {
              timer.current = undefined;
              telemetry.trackInteraction('tooltip_show', {
                tooltipId: id,
              });
            }, 1000);
          }}
          afterHide={() => {
            clearTimeout(timer.current);
            if (timer.current === undefined) {
              telemetry.trackInteraction('tooltip_hide', {
                tooltipId: id,
              });
            }
          }}
        />
      </TooltipPortal>
    </>
  );
}
const TooltipPortal = ({ children }: PropsWithChildren) => {
  return createPortal(children, document.body);
};
